import { useState, useMemo } from 'react'
import { useAuth } from '@clerk/clerk-react'
import type { PlayerRow, TeamLite } from '../types'
import { AvailabilityBadge } from './AvailabilityBadge'
import { usePlayerShortlist } from '../context/UserDataContext'

// Check if Clerk is configured
const clerkEnabled = !!(import.meta as any).env?.VITE_CLERK_PUBLISHABLE_KEY

type WindowKey = 'season' | 'last10' | 'last5' | 'last3'
type StatMode = 'total' | 'per90'
type SortKey = 'name' | 'position' | 'minutes' | 'goals' | 'assists' | 'shots_on_target' | 'shots' | 'fouls'
type SortDir = 'asc' | 'desc'

interface PlayerListProps {
  rows: PlayerRow[]
  window: WindowKey
  statMode?: StatMode
  team?: TeamLite
  compact?: boolean
  compareMode?: boolean
  selectedPlayers?: PlayerRow[]
  onPlayerSelect?: (player: PlayerRow) => void
  isPlayerSelected?: (playerId: number) => boolean
  confirmed?: boolean
  onPlayerClick?: (player: PlayerRow) => void
  // Legacy props for backwards compatibility
  title?: string
}

const COLUMNS: { key: SortKey; label: string; shortLabel: string; statKey?: string }[] = [
  { key: 'minutes', label: 'MP', shortLabel: 'MP', statKey: 'matches_played' },
  { key: 'minutes', label: 'Min', shortLabel: 'Min', statKey: 'minutes' },
  { key: 'shots_on_target', label: 'SoT', shortLabel: 'SoT', statKey: 'shots_on_target' },
  { key: 'shots', label: 'Sho', shortLabel: 'Sho', statKey: 'shots' },
  { key: 'fouls', label: 'FK', shortLabel: 'FK', statKey: 'fouls_drawn' },
]

export function PlayerList({
  rows,
  window,
  statMode = 'total',
  team,
  compact,
  compareMode,
  selectedPlayers = [],
  onPlayerSelect,
  isPlayerSelected,
  confirmed,
  onPlayerClick,
  title,
}: PlayerListProps) {
  const auth = clerkEnabled ? useAuth() : { isSignedIn: false }
  const { isSignedIn } = auth
  const { isShortlisted, toggleShortlist } = usePlayerShortlist()
  const [sortKey, setSortKey] = useState<SortKey>('shots')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      let aVal: string | number = 0
      let bVal: string | number = 0

      if (sortKey === 'name') {
        aVal = a.name.toLowerCase()
        bVal = b.name.toLowerCase()
      } else if (sortKey === 'position') {
        aVal = a.position || 'Z'
        bVal = b.position || 'Z'
      } else {
        const aStats = a.windows?.[window] || {}
        const bStats = b.windows?.[window] || {}
        const col = COLUMNS.find(c => c.key === sortKey)
        const statKey = col?.statKey || sortKey
        aVal = aStats[statKey] ?? 0
        bVal = bStats[statKey] ?? 0

        // Apply per90 conversion if needed
        if (statMode === 'per90' && sortKey !== 'minutes') {
          const aMins = aStats.minutes ?? 0
          const bMins = bStats.minutes ?? 0
          aVal = aMins > 0 ? ((aVal as number) / aMins) * 90 : 0
          bVal = bMins > 0 ? ((bVal as number) / bMins) * 90 : 0
        }
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      }

      return sortDir === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
    return sorted
  }, [rows, window, sortKey, sortDir, statMode])

  const getSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null
    return (
      <svg
        width="10"
        height="10"
        viewBox="0 0 24 24"
        fill="currentColor"
        style={{ marginLeft: 2, opacity: 0.7 }}
      >
        {sortDir === 'asc'
          ? <path d="M7 14l5-5 5 5H7z" />
          : <path d="M7 10l5 5 5-5H7z" />
        }
      </svg>
    )
  }

  const handleRowClick = (player: PlayerRow) => {
    if (compareMode && onPlayerSelect) {
      onPlayerSelect(player)
    } else if (!compareMode && onPlayerClick) {
      onPlayerClick(player)
    }
  }

  const canSelect = (playerId: number) => {
    if (!compareMode) return false
    const alreadySelected = isPlayerSelected?.(playerId) ?? false
    if (alreadySelected) return true
    return selectedPlayers.length < 3
  }

  const handleShortlistClick = async (e: React.MouseEvent, player: PlayerRow) => {
    e.stopPropagation()
    try {
      await toggleShortlist(player.player_id, player.name)
    } catch (err) {
      console.error('Failed to toggle shortlist:', err)
    }
  }


  const formatStat = (value: number | undefined, minutes: number | undefined, key: string): string => {
    if (value === undefined || value === null) return '—'

    if (statMode === 'per90' && key !== 'minutes' && key !== 'matches_played') {
      const mins = minutes ?? 0
      if (mins === 0) return '—'
      const per90 = (value / mins) * 90
      return per90.toFixed(2)
    }

    return typeof value === 'number' && !Number.isInteger(value)
      ? value.toFixed(1)
      : String(value)
  }

  const getPositionAbbrev = (position?: string | null): string => {
    if (!position) return '?'
    const pos = position.toUpperCase()
    if (pos.includes('GOALKEEPER') || pos.includes('GK')) return 'GK'
    if (pos.includes('DEFENDER') || pos.includes('DEF') || pos.includes('CB') || pos.includes('LB') || pos.includes('RB')) return 'DF'
    if (pos.includes('MIDFIELDER') || pos.includes('MID') || pos.includes('CM') || pos.includes('DM') || pos.includes('AM')) return 'MF'
    if (pos.includes('FORWARD') || pos.includes('FWD') || pos.includes('ST') || pos.includes('CF') || pos.includes('LW') || pos.includes('RW')) return 'ST'
    return position.charAt(0).toUpperCase()
  }

  // Legacy rendering for backwards compatibility
  if (title) {
    return (
      <div className="card" style={{ background: 'rgba(255,255,255,.03)' }}>
        <div className="card-inner">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>{title}</strong>
            <span className="pill">{compact ? 'bench' : 'XI'}</span>
          </div>
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                {compareMode && <th style={{ width: 28 }}></th>}
                {isSignedIn && !compareMode && <th style={{ width: 28 }}></th>}
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Player</th>
                <th onClick={() => handleSort('position')} style={{ cursor: 'pointer' }}>Pos</th>
                <th onClick={() => handleSort('minutes')} style={{ cursor: 'pointer' }}>Mins</th>
                <th onClick={() => handleSort('goals' as SortKey)} style={{ cursor: 'pointer' }}>Goals</th>
                <th onClick={() => handleSort('assists' as SortKey)} style={{ cursor: 'pointer' }}>Ast</th>
                <th onClick={() => handleSort('shots_on_target')} style={{ cursor: 'pointer' }}>SOT</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((p) => {
                const stats = p.windows?.[window] || {}
                const selected = isPlayerSelected?.(p.player_id) ?? false
                const selectable = canSelect(p.player_id)
                const isUnavailable = p.availability_status === 'injured' || p.availability_status === 'suspended' || p.availability_status === 'confirmed_out'

                return (
                  <tr
                    key={p.player_id}
                    onClick={() => handleRowClick(p)}
                    className={`${compareMode ? 'selectable' : ''} ${selected ? 'selected' : ''} ${compareMode && !selectable ? 'disabled' : ''} ${isUnavailable ? 'player-unavailable' : ''}`}
                    style={{ cursor: compareMode && selectable ? 'pointer' : undefined }}
                  >
                    {compareMode && (
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={selected} disabled={!selectable} onChange={() => handleRowClick(p)} onClick={(e) => e.stopPropagation()} className="player-checkbox" />
                      </td>
                    )}
                    {isSignedIn && !compareMode && (
                      <td style={{ textAlign: 'center' }}>
                        <button className={`shortlist-btn ${isShortlisted(p.player_id) ? 'shortlisted' : ''}`} onClick={(e) => handleShortlistClick(e, p)} title={isShortlisted(p.player_id) ? 'Remove from shortlist' : 'Add to shortlist'}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill={isShortlisted(p.player_id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                          </svg>
                        </button>
                      </td>
                    )}
                    <td className="player-name-cell">
                      <span className={isUnavailable ? 'player-name-unavailable' : ''}>{p.name}</span>
                      <AvailabilityBadge status={p.availability_status} reason={p.availability_reason} compact />
                    </td>
                    <td>{p.position?.charAt(0) ?? '?'}</td>
                    <td>{stats.minutes ?? '—'}</td>
                    <td>{stats.goals ?? '—'}</td>
                    <td>{stats.assists ?? '—'}</td>
                    <td>{stats.shots_on_target ?? '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // New v2 design
  return (
    <div className={`player-list-v2 ${compact ? 'compact' : ''}`}>
      {/* Table Header */}
      <div className="player-list-header">
        <div className="player-col player-col-name">Player</div>
        {COLUMNS.map((col, idx) => (
          <div
            key={`${col.key}-${idx}`}
            className={`player-col player-col-stat ${sortKey === col.key ? 'sorted' : ''}`}
            onClick={() => handleSort(col.key)}
          >
            {col.shortLabel}
            {getSortIndicator(col.key)}
          </div>
        ))}
      </div>

      {/* Player Rows */}
      <div className="player-list-body">
        {sortedRows.map((p) => {
          const stats = p.windows?.[window] || {}
          const selected = isPlayerSelected?.(p.player_id) ?? false
          const selectable = canSelect(p.player_id)
          const isUnavailable = p.availability_status === 'injured' || p.availability_status === 'suspended' || p.availability_status === 'confirmed_out'
          const isStarting = confirmed ? p.confirmed_starter : (p.start_probability ?? 0) >= 0.7

          return (
            <div
              key={p.player_id}
              className={`player-row-v2 ${compareMode ? 'selectable' : ''} ${selected ? 'selected' : ''} ${compareMode && !selectable ? 'disabled' : ''} ${isUnavailable ? 'unavailable' : ''} ${onPlayerClick && !compareMode ? 'clickable' : ''}`}
              onClick={() => handleRowClick(p)}
            >
              {/* Player Info Column */}
              <div className="player-col player-col-name">
                <div className="player-info-v2">
                  {/* Team Badge */}
                  {team?.crest_url && (
                    <img src={team.crest_url} alt="" className="player-team-badge" />
                  )}

                  {/* Player Name & Details */}
                  <div className="player-details">
                    <span className={`player-name-v2 ${isUnavailable ? 'unavailable' : ''}`}>
                      {p.name.split(' ').length > 1
                        ? `${p.name.split(' ')[0].charAt(0)}. ${p.name.split(' ').slice(1).join(' ')}`
                        : p.name
                      }
                    </span>
                    <div className="player-meta">
                      <span className="player-position-badge">{getPositionAbbrev(p.position)}</span>
                      {isStarting && (
                        <>
                          <span className="player-meta-sep">•</span>
                          <span className="player-starting-badge">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                              <circle cx="9" cy="7" r="4" />
                            </svg>
                            Starting
                          </span>
                        </>
                      )}
                      <AvailabilityBadge status={p.availability_status} reason={p.availability_reason} compact />
                    </div>
                  </div>

                  {/* Compare checkbox */}
                  {compareMode && (
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={!selectable}
                      onChange={() => handleRowClick(p)}
                      onClick={(e) => e.stopPropagation()}
                      className="player-checkbox-v2"
                    />
                  )}

                  {/* Shortlist button */}
                  {isSignedIn && !compareMode && (
                    <button
                      className={`shortlist-btn-v2 ${isShortlisted(p.player_id) ? 'active' : ''}`}
                      onClick={(e) => handleShortlistClick(e, p)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={isShortlisted(p.player_id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="player-row-divider" />

              {/* Stats Columns */}
              {COLUMNS.map((col, idx) => {
                const statKey = col.statKey || col.key
                return (
                  <div key={`${col.key}-${idx}`} className="player-col player-col-stat">
                    {formatStat(stats[statKey], stats.minutes, statKey)}
                  </div>
                )
              })}

              {/* Odds Button */}
              <div className="player-col player-col-action">
                <button className="player-odds-btn" onClick={(e) => e.stopPropagation()}>
                  Odds
                </button>
              </div>
            </div>
          )
        })}

        {sortedRows.length === 0 && (
          <div className="player-list-empty">
            No players to display
          </div>
        )}
      </div>
    </div>
  )
}
